import { FC, useEffect } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import Icon from '@/components/ui/Icon';
import CTAButton from '@/components/ui/CTAButton';
import useGoogleCalendarStore from '@/store/google';
import usePermissions from '@/hooks/usePermissions';
import { openGoogleAuth } from '@/utils/googleAuth';

/**
 * Kalender-Banner (Vorgabe Alex 2026-07-20): erscheint, sobald der eingeloggte
 * User KEINEN nutzbaren Google-Kalender hat. Zwei Auspraegungen desselben
 * Zustands:
 *
 *   - nie verbunden (kein Token)       -> „Kalender nicht verknuepft"
 *   - Token als tot markiert (invalid) -> „Google-Kalender getrennt" (Reconnect,
 *     google-token-health.md; frueher GoogleReconnectBanner)
 *
 * BEWUSST EINE KOMPONENTE: beide Faelle sind fachlich derselbe Zustand
 * („Self-Book geht nicht"). Zwei Banner nebeneinander koennten sich
 * widersprechen und wuerden bei Regel-Aenderungen auseinanderlaufen.
 *
 * STATUS-QUELLE = SELF-BOOK-GATE (eine Definition, 2026-07-12):
 * `isConnected` im Store ist `connected && !invalid` und damit das exakte
 * Spiegelbild von `User::googleCalendarUsable()` im Backend — genau das
 * Praedikat, das `CallcenterSelfBookingService::bookForSelf` als
 * Readiness-Gate prueft (GoogleCalendarRequiredException). Banner und
 * Buchbarkeit koennen sich dadurch nicht widersprechen. Wer das hier auf
 * `connected` allein umstellt, baut die „bereits verbunden"-Sackgasse wieder
 * ein: ein toter Token hat weiterhin eine Token-Zeile.
 *
 * ROLLEN — die Regel steht KOMPLETT hier (nicht geteilt mit der Mount-Stelle),
 * damit eine einzige Stelle beantwortet „wer sieht ein Kalender-Banner":
 *   - CC  : nie. Bucht ausschliesslich in den Pool, braucht keinen eigenen
 *           Kalender.
 *   - SA  : unveraendert nur der Reconnect-Fall. Ein SA ohne Kalender ist der
 *           Normalfall (Admin, nimmt keine Termine an) — kein neues Banner.
 *   - VP + TELEFONIE: beide Faelle. Self-Book setzt eine nutzbare Verbindung
 *           voraus; ohne Banner haette besonders ein frisch angelegter
 *           Onboarding-User keinen Weg, seinen Kalender zu verbinden, und
 *           „fuer mich buchen" bliebe dauerhaft eine Ablehnung.
 *
 * Nach erfolgreichem Connect laedt `checkStatus` den Status neu -> Banner
 * verschwindet ohne Re-Login.
 */
const GoogleCalendarBanner: FC = () => {
  const { isConnected, isInvalid, checkStatus } = useGoogleCalendarStore();
  const { isCC, isSA } = usePermissions();

  // CC-Guard IM Effect, nicht davor: Hooks duerfen nicht hinter einem
  // fruehen Return stehen. So bleibt CC auch im Netzwerk-Verhalten
  // unveraendert (frueher wurde die Komponente fuer CC gar nicht gemountet).
  useEffect(() => {
    if (isCC()) return;
    checkStatus();
  }, [checkStatus, isCC]);

  if (isCC()) return null;

  // Nicht nutzbar = kein Token ODER toter Token. Fuer SA bleibt es beim
  // bisherigen Verhalten (nur toter Token).
  const showNeverConnected = !isConnected && !isInvalid && !isSA();

  if (!isInvalid && !showNeverConnected) return null;

  const message = isInvalid
    ? 'Google-Kalender getrennt — du erhältst keine neuen Termine. Jetzt neu verbinden.'
    : 'Kalender nicht verknüpft — Buchungen für dich selbst sind erst nach dem Verbinden möglich.';

  return (
    <Box sx={{ bgcolor: 'error.light', color: 'error.contrastText', borderBottom: '1px solid', borderColor: 'divider', px: 8, py: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        flexWrap="wrap"
      >
        <Stack direction="row" alignItems="center" gap={2}>
          <Icon icon="Error" size={20} sx={{ color: 'error.contrastText' }} />
          <Typography variant="wertBetont" sx={{ color: 'error.contrastText' }}>{message}</Typography>
        </Stack>
        <CTAButton
          label={isInvalid ? 'Jetzt neu verbinden' : 'Jetzt verbinden'}
          variant="secondary"
          autoWidth
          onClick={() => openGoogleAuth(() => checkStatus())}
        />
      </Stack>
    </Box>
  );
};

export default GoogleCalendarBanner;
